import {
  type CreateReceiptScanInput,
  type Invalidations,
  type ReceiptScanJob,
  receiptScanKeys,
} from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { retrying } from '@/app/retrying'
import { useApi } from '@/app/api'
import { problemDetail } from '../internal/problem.ts'

const POLL_INTERVAL_MS = 2000

export interface UseReceiptScanJobsOptions {
  readonly ledgerId: () => string | undefined
  readonly enabled: () => boolean
}

export interface UseReceiptScanJobs {
  readonly jobs: ComputedRef<readonly ReceiptScanJob[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly isUploading: ComputedRef<boolean>
  readonly uploadErrorDetail: ComputedRef<string | undefined>
  readonly isDeleting: ComputedRef<boolean>
  retry(): void
  upload(input: CreateReceiptScanInput): Promise<ReceiptScanJob>
  /** Only a `FAILED` job accepts this - the server refuses every other status. */
  deleteJob(jobId: string): Promise<void>
}

function isWatching(jobs: readonly ReceiptScanJob[] | undefined): boolean {
  return (jobs ?? []).some((job) => job.status === 'PENDING')
}

export function useReceiptScanJobs(options: UseReceiptScanJobsOptions): UseReceiptScanJobs {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const listQuery = useQuery({
    queryKey: computed(() => receiptScanKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.receiptScans.list(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
    refetchInterval: (query) => (isWatching(query.state.data) ? POLL_INTERVAL_MS : false),
  })

  function invalidate(keys: Invalidations): Promise<void> {
    return Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))).then(
      () => undefined,
    )
  }

  const uploadMutation = useMutation({
    mutationFn: (input: CreateReceiptScanInput) => api.receiptScans.create(options.ledgerId()!, input),
    onSuccess: () => invalidate(receiptScanKeys.invalidatedByWrite(options.ledgerId()!)),
  })

  const deleteMutation = useMutation({
    mutationFn: (jobId: string) => api.receiptScans.delete(options.ledgerId()!, jobId),
    onSuccess: () => invalidate(receiptScanKeys.invalidatedByDelete(options.ledgerId()!)),
  })

  return {
    jobs: computed(() => listQuery.data.value ?? []),
    isLoading: computed(() => active.value && listQuery.isPending.value),
    error: computed(() => listQuery.error.value),
    retrying: retrying(listQuery),
    isUploading: computed(() => uploadMutation.isPending.value),
    uploadErrorDetail: computed(() => problemDetail(uploadMutation.error.value)),
    isDeleting: computed(() => deleteMutation.isPending.value),
    retry: () => {
      void listQuery.refetch()
    },
    upload: (input) => uploadMutation.mutateAsync(input),
    deleteJob: (jobId) => deleteMutation.mutateAsync(jobId),
  }
}
